import * as React from "react";
import {
  Button,
  DatePicker,
  Dropdown,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ReactNode } from "react";
import {
  BarsOutlined,
  InfoCircleOutlined,
  LockOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

import {
  BandStrategy,
  DateStrategy,
  IsoDayOfWeek,
  ProductId,
} from "app/shared/model/EditProductOffering";
import { normIso } from "../currency/normalizeCurrency";
import type { PricingYearId } from "app/shared/model";
import type {
  DateStrategyAssign,
  SchoolStatusDto,
  SchoolOfferingStatus,
  PackageOfferingBreakdown,
  PriceBands,
  PricePeriods,
} from "app/shared/model/EditPricelist";
import type { FormError } from "app/shared/model/Form";
import FormField, { SelectAllOnFocusInput } from "app/shared/components/Form/FormField";

import type { FlatRow, NonFeeOfferingDto } from "./types";
import {
  buildBreakdownColorMap,
  buildBreakdownVMs,
} from "../packageBreakdowns/helpers";
import { productTypeNameFromId } from "../offering/productType";
import { buildNonFeeOfferingKey } from "../offering/keys";

import { BreakdownLegendLine as DefaultBreakdownLegendLine } from "../../components/breakdowns/BreakdownLegendLine";
import type { BreakdownLegendLineProps } from "../../components/breakdowns/BreakdownLegendLine";
import {cloneDeep} from "app/pages/PricelistNew/components/utils/cloneDeep";
import { formatRecurranceStartDaysLabel } from "app/pages/PricelistNew/domain/dateStrategy/recurranceDays";
import {PP_COL_W} from "app/pages/PricelistNew/domain/tables/ppColumnWidths";
import { PRICE_MAJOR_MINOR_UNIT_TOOLTIP } from "app/pages/PricelistNew/domain/tables/priceColumnTooltips";
import { isDailyUnitType } from "app/pages/PricelistNew/domain/tables/unitTypeRules";
import {shiftPickerDateToEarliestAllowed} from "app/pages/PricelistNew/domain/pricingYear/window";
import { OpenProductLink } from "app/shared/products/OpenProductLink";
import { SchoolsEditPopover } from "./SchoolsEditPopover";
import { useIsOfferingEditing } from "./EditModeContext";
import { isAnyUnits } from "../priceBands/priceBandsNormalize";

const DATE_PICKER_SELECT_ALL_COMPONENTS = { input: SelectAllOnFocusInput };

function offeringKeyOf(row: FlatRow): string {
  return buildNonFeeOfferingKey(row.product as any);
}

const PricelistFormField: React.FC<
  Omit<React.ComponentProps<typeof FormField>, "editMode"> & {
    rowEditable: boolean;
    offeringKey: string;
  }
> = ({ rowEditable, offeringKey, ...rest }) => {
  const offeringEditing = useIsOfferingEditing(offeringKey);
  return (
    <FormField
      {...rest}
      editMode={offeringEditing && rowEditable}
      standalone={rest.standalone ?? true}
      immediateEdit={offeringEditing && rowEditable}
    />
  );
};

const LazyDateWhen: React.FC<{
  rowEditable?: boolean;
  offeringKey: string;
  idleContent: React.ReactNode;
  activeContent: React.ReactNode;
}> = ({ rowEditable = true, offeringKey, idleContent, activeContent }) => {
  const offeringEditing = useIsOfferingEditing(offeringKey);
  const canEdit = offeringEditing && rowEditable;
  if (canEdit) return <>{activeContent}</>;
  return <>{idleContent}</>;
};

const parseDDMMYYYY = (value: string) => {
  const trimmed = value.replace(/\s/g, "");
  if (!/^\d{8}$/.test(trimmed)) return null;
  const parsed = dayjs(trimmed, "DDMMYYYY", true);
  return parsed.isValid() ? parsed : null;
};

const { Text } = Typography;

export interface CreateProductTableColumnsParams {
  editMode?: boolean;
  selectedProductType: string;
  pricingYears: PricingYearId[];
  selectedPricingYear?: number;
  formErrors?: FormError[];
  enabledCurrencies: string[];
  centreSchools: { id: number; name: string }[];
  breakdownNameMap: Map<string, string>;
  standaloneFields?: boolean;

  handleChange: (
    productId: ProductId,
    fieldName: string,
    value: any,
    collection: any,
    index?: number,
    periodIndexOrCurrency?: number | string,
    currencyIsoCode?: string
  ) => void;
  handlePriceChange: (
    productId: ProductId,
    periodIndex: number,
    bandIndex: number,
    currencyIsoCode: string,
    fieldName: "pricePerMajorUnit" | "pricePerMinorUnit",
    value: any
  ) => void;
  onSchoolToggle: (
    offering: NonFeeOfferingDto,
    schoolId: number,
    nextChecked: boolean
  ) => void;
  handleError: (errors: FormError[]) => void;

  generateContextMenu: (
    hasPendingChanges: boolean,
    product: NonFeeOfferingDto,
    period: PricePeriods,
    band: PriceBands,
    pi: number,
    ppi: number,
    pbi: number,
    isPricePeriod: boolean
  ) => ReactNode;

  openBreakdownsModal: (product: NonFeeOfferingDto) => void;
  openDecommissionModal: (product: NonFeeOfferingDto) => void;
  openRecommissionModal: (product: NonFeeOfferingDto) => void;
  openExcludeModal: (product: NonFeeOfferingDto) => void;

  setSelectedProduct: (product: NonFeeOfferingDto) => void;
  setDateStrategies: (strategies: DateStrategyAssign[]) => void;
  setShowModal: (show: boolean) => void;
  setPendingChanges: (pending: boolean) => void;

  BreakdownLegendLine?: React.ComponentType<BreakdownLegendLineProps>;
  rowHighlightColors?: Record<string, string>;
  onRowHighlightChange?: (offeringKey: string, color: string | null) => void;
  allowedStartDate: string;
  allowedEndDate: string;
}

const MAX_UNIT = "999.99";
const MAX_PRICE = "999999.99";

const DATE_DISPLAY_FORMAT = "DD-MMM-YYYY";
const DATE_PICKER_FORMATS: [string, string] = [DATE_DISPLAY_FORMAT, "DDMMYYYY"];
const DATE_STORE_FORMAT = "YYYY-MM-DD";

export function createProductTableColumns(
  params: CreateProductTableColumnsParams
): any[] {
  const {
    selectedProductType,
    pricingYears,
    selectedPricingYear,
    formErrors = [],
    enabledCurrencies,
    centreSchools,
    breakdownNameMap,
    standaloneFields = false,
    handleChange,
    handleError,
    generateContextMenu,
    openBreakdownsModal,
    openDecommissionModal,
    openRecommissionModal,
    openExcludeModal,
    setSelectedProduct,
    setDateStrategies,
    setShowModal,
    setPendingChanges,
    handlePriceChange,
    onSchoolToggle,
    allowedStartDate,
    allowedEndDate,
  } = params;

  const BreakdownLegendLine =
    params.BreakdownLegendLine ?? DefaultBreakdownLegendLine;
  const rowHighlightColors = params.rowHighlightColors;
  const onRowHighlightChange = params.onRowHighlightChange;

  function formatDateStrategy(product: NonFeeOfferingDto, rowHasPendingChanges: boolean) {
    const all: any[] = Array.isArray((product as any).dateStrategyAssignments)
        ? (product as any).dateStrategyAssignments
        : [];

    const yearRecord = pricingYears.find((y) => (y as any).id === selectedPricingYear);
    const selectedYear = yearRecord ? (yearRecord as any).year : new Date().getFullYear();

    const inYear = all.filter((ds: any) => {
      const from = dayjs(ds?.fromDate);
      if (!from.isValid()) return false;

      const to = ds?.toDate ? dayjs(ds.toDate) : null;
      return from.year() <= selectedYear && (!to || !to.isValid() || to.year() >= selectedYear);
    });

    const isArchivedStrategy = (ds: any) =>
      Boolean(ds?.isArchived ?? ds?.IsArchived);

    const inYearActive = inYear.filter((ds: any) => !isArchivedStrategy(ds));

    const strategyLabel = (ds: any): string | null => {
      const name = DateStrategy[ds.strategyTypeId as any];
      if (!name) return null;

      if (name === "Recurrence") {
        const days = formatRecurranceStartDaysLabel(ds.recurranceStartDays);
        return days ? `Recurrence every ${days}` : "Recurrence";
      }
      return name;
    };

    const activeParts: string[] = [];
    inYearActive.forEach((ds: any) => {
      const label = strategyLabel(ds);
      if (label && !activeParts.includes(label)) activeParts.push(label);
    });

    const allArchived =
      inYear.length > 0 && inYear.every(isArchivedStrategy);

    let display: React.ReactNode;
    if (activeParts.length > 0) {
      display = <span>{activeParts.join(" / ")}</span>;
    } else if (allArchived) {
      display = (
        <span style={{ color: "rgba(0, 0, 0, 0.45)" }}>
          <Tag style={{ marginRight: 6 }}>Archived</Tag>
          —
        </span>
      );
    } else {
      display = "—";
    }

    const canOpen = !rowHasPendingChanges;

    return canOpen ? (
        <a
            className="pointer"
            onClick={() => {
              setSelectedProduct(product);
              setDateStrategies(cloneDeep(all));
              setShowModal(true);
              setPendingChanges(false);
            }}
        >
          {display}
        </a>
    ) : (
        display
    );
  }

  const schoolOptions = centreSchools.map((school) => ({
    label: school.name,
    value: school.id,
  }));
  const schoolNameMap = new Map(
    centreSchools.map((school) => [school.id, school.name])
  );

  const resolveBandPrice = (band: any, iso: string) => {
    const code = normIso(iso);
    if (!code) return undefined;
    const prices = Array.isArray(band?.prices) ? band.prices : [];
    return prices.find((p: any) => normIso(p?.currencyIsoCode) === code);
  };

  const defaultPriceUnit = (value: unknown): number => {
    if (value == null || value === "") return 0;
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const getSchoolKey = (input: any): number | null => {
    if (input == null) return null;
    if (typeof input === "object") {
      const c: any = input;
      if ("id" in c) return getSchoolKey(c.id);
      if ("Id" in c) return getSchoolKey(c.Id);
      if ("schoolId" in c) return getSchoolKey(c.schoolId);
      if ("SchoolId" in c) return getSchoolKey(c.SchoolId);
    }
    const n = Number(input);
    return Number.isFinite(n) ? n : null;
  };

  const buildStatusMap = (offering: any): Map<number, SchoolStatusDto> => {
    const map = new Map<number, SchoolStatusDto>();
    (offering?.schoolStatuses ?? []).forEach((item: SchoolStatusDto) => {
      const id = getSchoolKey((item as any)?.schoolId);
      if (id != null) map.set(id, item);
    });
    return map;
  };

  const resolveStatus = (
    statusMap: Map<number, SchoolStatusDto>,
    schoolId: number
  ): SchoolOfferingStatus => {
    return statusMap.get(schoolId)?.status ?? "Disabled";
  };

  const formatDecommissionDate = (value?: any) => {
    const d = dayjs(value);
    return d.isValid() ? d.format("DD-MMM-YYYY") : "-";
  };

  const minIso = (a?: string | null, b?: string | null) => {
    if (!a) return b ?? "";
    if (!b) return a;
    return a < b ? a : b;
  };

  const maxIso = (a?: string | null, b?: string | null) => {
    if (!a) return b ?? "";
    if (!b) return a;
    return a > b ? a : b;
  };

  const priceColumns = (enabledCurrencies ?? []).map((currencyIso) => ({
    title:
      selectedProductType === "AddOn" ? (
        <div>
          {`Price (${currencyIso})`}{" "}
          <Tooltip
            title={
              <div style={{ whiteSpace: "pre-line" }}>
                {PRICE_MAJOR_MINOR_UNIT_TOOLTIP}
              </div>
            }
          >
            <InfoCircleOutlined />
          </Tooltip>
        </div>
      ) : (
        `Price (${currencyIso})`
      ),
    children: [
      {
        title: "Major",
        dataIndex: ["band", "PricePerMajorUnit"],
        width: PP_COL_W.price,
        render: (_: any, row: FlatRow) => {
          const price = resolveBandPrice(row.band as any, currencyIso);
          return (
            <PricelistFormField
              key={`priceMajorUnit-${currencyIso}-${row.pi}-${row.ppi}-${row.pbi}`}
              name={`priceMajorUnit-${currencyIso}-${row.pi}-${row.ppi}-${row.pbi}`}
              offeringKey={offeringKeyOf(row)}
              rowEditable={(row.product as any).hasPendingChanges === false}
              type="number"
              greaterThan="0"
              max={MAX_PRICE}
              decimalPlace={2}
              commitOnBlur
              selectAllOnFirstFocus
              defaultValue={defaultPriceUnit(price?.pricePerMajorUnit)}
              required
              formErrors={formErrors}
              standalone={standaloneFields}
              onError={handleError}
              onChange={(value) => {
                handlePriceChange(
                  (row.product as any).productId,
                  row.ppi,
                  row.pbi,
                  currencyIso,
                  "pricePerMajorUnit",
                  value
                );
              }}
            />
          );
        },
      },
      {
        title: "Minor",
        dataIndex: ["band", "PricePerMinorUnit"],
        width: PP_COL_W.price,
        render: (_: any, row: FlatRow) => {
          const product = row.product as any;
          if (
            selectedProductType === "AddOn" &&
            isDailyUnitType(product.unitTypeId, product.unitTypeName ?? product.unitTypeText)
          ) {
            return <Text type="secondary">N/A</Text>;
          }

          const price = resolveBandPrice(row.band as any, currencyIso);
          return (
            <PricelistFormField
              key={`priceMinorUnit-${currencyIso}-${row.pi}-${row.ppi}-${row.pbi}`}
              name={`priceMinorUnit-${currencyIso}-${row.pi}-${row.ppi}-${row.pbi}`}
              offeringKey={offeringKeyOf(row)}
              rowEditable={(row.product as any).hasPendingChanges === false}
              type="number"
              greaterThan="0"
              max={MAX_PRICE}
              decimalPlace={2}
              commitOnBlur
              selectAllOnFirstFocus
              defaultValue={defaultPriceUnit(price?.pricePerMinorUnit)}
              required
              formErrors={formErrors}
              standalone={standaloneFields}
              onError={handleError}
              onChange={(value) => {
                handlePriceChange(
                  (row.product as any).productId,
                  row.ppi,
                  row.pbi,
                  currencyIso,
                  "pricePerMinorUnit",
                  value
                );
              }}
            />
          );
        },
      },
    ],
  }));

  const highlightColumn =
    onRowHighlightChange && rowHighlightColors
      ? {
          title: "",
          dataIndex: "highlight",
          width: 44,
          align: "center" as const,
          render: (_: any, row: FlatRow) => {
            if (row.ppi === 0 && row.pbi === 0) {
              const key = buildNonFeeOfferingKey(row.product as any);
              const color = rowHighlightColors[key] ?? "";
              const content = (
                <div className="pp-row-highlight-wrap">
                  <input
                    type="color"
                    className="pp-row-highlight-input"
                    value={color || "#e0e0e0"}
                    onChange={(e) =>
                      onRowHighlightChange(key, e.target.value || null)
                    }
                    title="Row highlight"
                  />
                  {color ? (
                    <Button
                      type="link"
                      size="small"
                      className="pp-row-highlight-clear"
                      onClick={() => onRowHighlightChange(key, null)}
                    >
                      Clear
                    </Button>
                  ) : null}
                </div>
              );
              return {
                children: content,
                props: { rowSpan: row.productRowSpan },
              };
            }
            return { children: null, props: { rowSpan: 0 } };
          },
        }
      : null;

  const selectedPricingYearValue =
      pricingYears.find((py) => (py as any).id === selectedPricingYear)?.year;

  const isStrategyInSelectedYear = (ds: any): boolean => {
    const from = dayjs(ds?.fromDate);
    if (!from.isValid()) return false;

    if (!selectedPricingYearValue) return true;

    const to = ds?.toDate ? dayjs(ds.toDate) : null;

    return (
        from.year() <= selectedPricingYearValue &&
        (!to || !to.isValid() || to.year() >= selectedPricingYearValue)
    );
  };

  const getOfferingStartFrom = (product: NonFeeOfferingDto) => {
    const strategies = Array.isArray((product as any)?.dateStrategyAssignments)
        ? (product as any).dateStrategyAssignments
            .filter((x: any) => x?.isArchived !== true)
            .filter(isStrategyInSelectedYear)
        : [];

    const hasOfferingDatesStrategy = strategies.some(
        (x: any) => Number(x?.strategyTypeId) === 3
    );

    const strategyDates = strategies
        .map((x: any) => dayjs(x?.fromDate))
        .filter((d: any) => d.isValid())
        .sort((a: any, b: any) => a.valueOf() - b.valueOf());

    const pricePeriodDates = (
        Array.isArray((product as any)?.pricePeriods)
            ? (product as any).pricePeriods
            : []
    )
        .map((x: any) => dayjs(x?.startDate))
        .filter((d: any) => {
          if (!d.isValid()) return false;
          if (!selectedPricingYearValue) return true;
          return d.year() === selectedPricingYearValue;
        })
        .sort((a: any, b: any) => a.valueOf() - b.valueOf());

    const strategyStart = strategyDates[0]?.startOf("day") ?? null;
    const pricePeriodStart = pricePeriodDates[0]?.startOf("day") ?? null;

    return hasOfferingDatesStrategy
        ? pricePeriodStart ?? strategyStart
        : strategyStart ?? pricePeriodStart;
  };

  const hasOfferingStarted = (product: NonFeeOfferingDto) => {
    const startFrom = getOfferingStartFrom(product);
    const today = dayjs().startOf("day");
    return !!startFrom && !today.isBefore(startFrom, "day");
  };

  const columns: any[] = [
    ...(highlightColumn ? [highlightColumn] : []),
    {
      title: "",
      dataIndex: "actions",
      width: PP_COL_W.actions,
      align: "center" as const,
      render: (_: any, row: FlatRow) => {
        if (row.ppi === 0 && row.pbi === 0) {
          const statusList = Array.isArray((row.product as any).schoolStatuses)
              ? (row.product as any).schoolStatuses
              : [];

          const hasLiveSchools = statusList.some((s: any) => s?.status === "Active");
          const hasDecommissionedSchools = statusList.some(
              (s: any) => s?.status === "Inactive"
          );

          const canDecommission =
              hasLiveSchools && hasOfferingStarted(row.product);

          const canRecommission =
              !hasLiveSchools && hasDecommissionedSchools;

          const menuItems: any[] = [];

          if (canDecommission) {
            menuItems.push({
              key: "actions-decommission",
              label: "Decommission",
              onClick: () => openDecommissionModal(row.product),
            });
          }

          if (canRecommission) {
            menuItems.push({
              key: "actions-recommission",
              label: "Recommission",
              onClick: () => openRecommissionModal(row.product),
            });
          }

          menuItems.push({
            key: "actions-exclude",
            label: "Exclude",
            onClick: () => openExcludeModal(row.product),
          });

          return {
            children: (
                <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
                  <Button
                      type="text"
                      icon={<BarsOutlined />}
                      aria-label="Product actions"
                      disabled={(row.product as any).hasPendingChanges}
                  />
                </Dropdown>
            ),
            props: { rowSpan: row.productRowSpan },
          };
        }

        return { children: null, props: { rowSpan: 0 } };
      },
    },
    {
      title: "Offering Name",
      dataIndex: ["product", "Name"],
      width: PP_COL_W.productName,
      render: (_: any, row: FlatRow) => {
        if (row.ppi === 0 && row.pbi === 0) {
          const statusMap = buildStatusMap(row.product);
          const statusValues = [...statusMap.values()];
          const allInactive =
            statusValues.length > 0 &&
            statusValues.every((s) => s.status === "Inactive");
          const latestInactiveDate = allInactive
            ? statusValues
                .map((s) => s.decommissionDate)
                .filter(Boolean)
                .sort()
                .slice(-1)[0]
            : undefined;

          return {
            children: (
              <div className="product-name-cell">
                <div className="product-name-header">
                  <div className="product-name-title">
                    {(row.product as any).isNew ? (
                      <Tag color="green" style={{ marginBottom: 4 }}>
                        New
                      </Tag>
                    ) : null}
                    {!(row.product as any).isNew &&
                    (row.product as any).isUpdated ? (
                      <Tag color="blue" style={{ marginBottom: 4 }}>
                        Updated
                      </Tag>
                    ) : null}
                    <OpenProductLink
                      productId={(row.product as any).productId?.id}
                      productType={
                        (row.product as any).productId?.type?.name ??
                        selectedProductType
                      }
                    >
                      {(row.product as any).name}
                    </OpenProductLink>
                    {(row.product as any).hasPendingChanges === true ? (
                      <LockOutlined style={{ marginLeft: 4 }} />
                    ) : null}
                  </div>
                  <div className="product-id-label">
                    ID: {(row.product as any).productId?.id ?? "—"}
                  </div>
                </div>
                {allInactive ? (
                  <div className="product-decommission-all">
                    Decommissioned for all schools - from {" "}
                    {formatDecommissionDate(latestInactiveDate)}
                  </div>
                ) : null}
              </div>
            ),
            props: { rowSpan: row.productRowSpan },
          };
        }
        return { children: null, props: { rowSpan: 0 } };
      },
    },
    {
      title: (
        <div>
          Date Range{" "}
          <Tooltip title="Date range period for this price band">
            <InfoCircleOutlined />
          </Tooltip>
        </div>
      ),
      children: [
        {
          title: "",
          dataIndex: "",
          width: 30,
          render: (_: any, row: FlatRow) =>
            row.pbi === 0
              ? {
                  children: generateContextMenu(
                    (row.product as any).hasPendingChanges,
                    row.product,
                    row.period,
                    row.band,
                    row.pi,
                    row.ppi,
                    row.pbi,
                    true
                  ),
                  props: { rowSpan: row.periodRowSpan },
                }
              : { children: null, props: { rowSpan: 0 } },
        },
        {
          title: "From",
          className: "pp-date-cell",
          dataIndex: ["period", "startDate"],
          width: PP_COL_W.date,
          render: (_: any, row: FlatRow) => {
            if (row.pbi === 0) {
              const start = (row.period as any).startDate;
              const end = (row.period as any).endDate;

              const maxForStartIso = end
                  ? minIso(String(end), allowedEndDate)
                  : allowedEndDate;

              const applyTypedStartDate = (raw: string) => {
                const parsed = parseDDMMYYYY(raw);
                if (!parsed) return;

                const normalizedIso = shiftPickerDateToEarliestAllowed(
                  parsed,
                  allowedStartDate,
                  maxForStartIso
                );
                if (!normalizedIso) return;

                handleChange(
                  (row.product as any).productId,
                  "startDate",
                  normalizedIso,
                  (row.product as any).pricePeriods,
                  row.ppi
                );

                const currentEndIso = end ? String(end) : null;
                if (currentEndIso && currentEndIso < normalizedIso) {
                  handleChange(
                    (row.product as any).productId,
                    "endDate",
                    normalizedIso,
                    (row.product as any).pricePeriods,
                    row.ppi
                  );
                }
              };

              return {
                children: (
                  <LazyDateWhen
                    offeringKey={offeringKeyOf(row)}
                    idleContent={
                      <span>{start ? dayjs(start).format(DATE_DISPLAY_FORMAT) : "—"}</span>
                    }
                    activeContent={
                    <DatePicker
                        components={DATE_PICKER_SELECT_ALL_COMPONENTS}
                        value={start ? dayjs(start) : null}
                        format={DATE_PICKER_FORMATS}
                        disabledDate={(date) =>
                            date.isBefore(dayjs(allowedStartDate), "day") ||
                            date.isAfter(dayjs(maxForStartIso), "day")
                        }
                        onChange={(date) => {
                          if (!date) {
                            handleChange(
                                (row.product as any).productId,
                                "startDate",
                                null,
                                (row.product as any).pricePeriods,
                                row.ppi
                            );
                            return;
                          }

                          const normalizedIso = shiftPickerDateToEarliestAllowed(
                              date,
                              allowedStartDate,
                              maxForStartIso
                          );

                          handleChange(
                              (row.product as any).productId,
                              "startDate",
                              normalizedIso,
                              (row.product as any).pricePeriods,
                              row.ppi
                          );

                          const currentEndIso = end ? String(end) : null;
                          if (currentEndIso && currentEndIso < normalizedIso) {
                            handleChange(
                                (row.product as any).productId,
                                "endDate",
                                normalizedIso,
                                (row.product as any).pricePeriods,
                                row.ppi
                            );
                          }
                        }}
                        onBlur={(e) =>
                          applyTypedStartDate((e.target as HTMLInputElement)?.value ?? "")
                        }
                        onKeyDown={(e) => {
                          if (e.key !== "Enter") return;
                          const value = (e.target as HTMLInputElement)?.value ?? "";
                          if (!/^\d{8}$/.test(value.replace(/\s/g, ""))) return;
                          e.preventDefault();
                          applyTypedStartDate(value);
                        }}
                        style={{ width: "100%" }}
                        allowClear
                    />
                    }
                  />
                ),
                props: { rowSpan: row.periodRowSpan },
              };
            }

            return { children: null, props: { rowSpan: 0 } };
          },
        },
        {
          title: "To",
          className: "pp-date-cell",
          dataIndex: ["period", "endDate"],
          width: PP_COL_W.date,
          render: (_: any, row: FlatRow) => {
            if (row.pbi === 0) {
              const start = (row.period as any).startDate;
              const end = (row.period as any).endDate;

              const minForEndIso = maxIso(
                  start ? String(start) : null,
                  allowedStartDate
              );

              const applyTypedEndDate = (raw: string) => {
                const parsed = parseDDMMYYYY(raw);
                if (!parsed) return;

                const normalizedIso = shiftPickerDateToEarliestAllowed(
                  parsed,
                  minForEndIso,
                  allowedEndDate
                );
                if (!normalizedIso) return;

                handleChange(
                  (row.product as any).productId,
                  "endDate",
                  normalizedIso,
                  (row.product as any).pricePeriods,
                  row.ppi
                );
              };

              return {
                children: (
                  <LazyDateWhen
                    offeringKey={offeringKeyOf(row)}
                    idleContent={
                      <span>{end ? dayjs(end).format(DATE_DISPLAY_FORMAT) : "—"}</span>
                    }
                    activeContent={
                    <DatePicker
                        components={DATE_PICKER_SELECT_ALL_COMPONENTS}
                        value={end ? dayjs(end) : null}
                        format={DATE_PICKER_FORMATS}
                        disabledDate={(date) =>
                            date.isBefore(dayjs(minForEndIso), "day") ||
                            date.isAfter(dayjs(allowedEndDate), "day")
                        }
                        onChange={(date) => {
                          if (!date) {
                            handleChange(
                                (row.product as any).productId,
                                "endDate",
                                null,
                                (row.product as any).pricePeriods,
                                row.ppi
                            );
                            return;
                          }

                          const normalizedIso = shiftPickerDateToEarliestAllowed(
                              date,
                              minForEndIso,
                              allowedEndDate
                          );

                          handleChange(
                              (row.product as any).productId,
                              "endDate",
                              normalizedIso,
                              (row.product as any).pricePeriods,
                              row.ppi
                          );
                        }}
                        onBlur={(e) =>
                          applyTypedEndDate((e.target as HTMLInputElement)?.value ?? "")
                        }
                        onKeyDown={(e) => {
                          if (e.key !== "Enter") return;
                          const value = (e.target as HTMLInputElement)?.value ?? "";
                          if (!/^\d{8}$/.test(value.replace(/\s/g, ""))) return;
                          e.preventDefault();
                          applyTypedEndDate(value);
                        }}
                        style={{ width: "100%" }}
                        allowClear
                    />
                    }
                  />
                ),
                props: { rowSpan: row.periodRowSpan },
              };
            }

            return { children: null, props: { rowSpan: 0 } };
          },
        }
      ],
    },
    ...(selectedProductType !== "Transfer"
      ? [
          {
            title: (
              <div>
                Weeks Bracket{" "}
                <Tooltip title="Weeks bracket for this price band">
                  <InfoCircleOutlined />
                </Tooltip>
              </div>
            ),
            children: [
              {
                title: "",
                dataIndex: "",
                width: 30,
                align: "center" as const,
                render: (_: any, row: FlatRow) =>
                  generateContextMenu(
                    (row.product as any).hasPendingChanges,
                    row.product,
                    row.period,
                    row.band,
                    row.pi,
                    row.ppi,
                    row.pbi,
                    false
                  ),
              },
              {
                title: "From",
                dataIndex: ["band", "MinUnits"],
                width: PP_COL_W.weeks,
                render: (_: any, row: FlatRow) => (
                  <PricelistFormField
                    key={`minUnits-${row.pi}-${row.ppi}-${row.pbi}`}
                    name={`minUnits-${row.pi}-${row.ppi}-${row.pbi}`}
                    offeringKey={offeringKeyOf(row)}
                    rowEditable={(row.product as any).hasPendingChanges === false}
                    type="number"
                    greaterThan="1"
                    max={MAX_UNIT}
                    defaultValue={(row.band as any).minUnits}
                    required
                    commitOnBlur
                    formErrors={formErrors}
                    standalone={standaloneFields}
                    onError={handleError}
                    onChange={(value) => {
                      handleChange(
                        (row.product as any).productId,
                        "minUnits",
                        value,
                        (row.period as any).priceBands,
                        row.pbi,
                        row.ppi
                      );
                    }}
                  />
                ),
              },
              {
                title: "To",
                dataIndex: "maxUnits",
                width: PP_COL_W.weeksTo,
                key: "maxUnits",
                render: (_: any, row: FlatRow) => {
                  const bandsCount = Array.isArray((row.period as any)?.priceBands)
                    ? (row.period as any).priceBands.length
                    : 0;

                  const isLastBand = bandsCount > 0 && row.pbi === bandsCount - 1;

                  const allowAnyHere =
                    isLastBand || isAnyUnits((row.band as any).maxUnits);

                  return (
                    <PricelistFormField
                      key={`maxUnits-${row.pi}-${row.ppi}-${row.pbi}`}
                      name={`maxUnits-${row.pi}-${row.ppi}-${row.pbi}`}
                      offeringKey={offeringKeyOf(row)}
                      rowEditable={(row.product as any).hasPendingChanges === false}
                      type="number"
                      greaterThan={String((row.band as any).minUnits)}
                      allowAny={allowAnyHere}
                      defaultValue={(row.band as any).maxUnits}
                      required
                      commitOnBlur
                      formErrors={formErrors}
                      standalone={standaloneFields}
                      onError={handleError}
                      onChange={(value) => {
                        handleChange(
                          (row.product as any).productId,
                          "maxUnits",
                          value,
                          (row.period as any).priceBands,
                          row.pbi,
                          row.ppi
                        );
                      }}
                    />
                  );
                },
              },
            ],
          },
        ]
      : []),
    ...priceColumns,
    {
      title: "Schools",
      dataIndex: ["product", "schoolIds"],
      width: PP_COL_W.schools,
      render: (_: any, row: FlatRow) => {
        if (row.ppi === 0 && row.pbi === 0) {
          const statusMap = buildStatusMap(row.product);
          const selectedIdsEdit = Array.isArray((row.product as any).schoolIds)
            ? (row.product as any).schoolIds
                .map((s: any) => getSchoolKey(s))
                .filter((id: any): id is number => id != null)
            : [];
          const selectedIdsView = [...statusMap.entries()]
            .filter(
              ([, status]) =>
                status.status === "Active" ||
                status.status === "ScheduledInactive"
            )
            .map(([id]) => id);
          const selectedIds = selectedIdsEdit;

          const statusEntries = centreSchools.map((school) => {
            const status =
              statusMap.get(school.id) ??
              ({
                schoolId: { id: school.id },
                status: "Disabled",
                isDeleted: false,
              } as any);
            return {
              id: school.id,
              name: school.name,
              status,
            };
          });

          const scheduledList = statusEntries.filter(
            (s) => s.status.status === "ScheduledInactive"
          );
          const inactiveList = statusEntries.filter(
            (s) => s.status.status === "Inactive"
          );
          const missingList = statusEntries.filter(
            (s) => s.status.status === "Disabled"
          );
          const activeList = statusEntries.filter(
            (s) => s.status.status === "Active"
          )

          const renderStatusTag = (status: SchoolOfferingStatus) => {
            const cls =
              status === "Active"
                ? "school-status-tag school-status-active"
                : status === "Inactive"
                  ? "school-status-tag school-status-inactive"
                  : status === "ScheduledInactive"
                    ? "school-status-tag school-status-scheduled"
                    : "school-status-tag school-status-missing";
            return <Tag className={cls}>{status}</Tag>;
          };

          const compactTooltip = statusEntries
            .map((entry) => {
              const status = entry.status.status as SchoolOfferingStatus;
              const detail =
                status === "ScheduledInactive"
                  ? `Scheduled decommission: ${formatDecommissionDate(
                      entry.status.decommissionDate
                    )}`
                  : status === "Inactive"
                    ? `Decommissioned: ${formatDecommissionDate(
                        entry.status.decommissionDate
                      )}`
                    : status === "Disabled"
                      ? "Not created"
                      : "Active";
              return `${entry.name} (${detail})`;
            })
            .join("\n");

          const firstSchoolName = activeList[0]?.name ?? statusEntries[0]?.name ?? "School";

          const viewChild =
            centreSchools.length > 3 ? (
              <Tooltip title={<div style={{ whiteSpace: "pre-line" }}>{compactTooltip}</div>}>
                <span>
                  {activeList.length > 1 ? `${firstSchoolName}...` : firstSchoolName}
                  {(activeList.length > 0 && inactiveList.length === 0 && scheduledList.length == 0 && missingList.length === 0) ? (
                     <Text type="secondary" style={{ marginLeft: 6 }}>
                      (+{activeList.length-1} more)
                    </Text> 
                  ) : null}
                  {inactiveList.length > 0 ? (
                    <Text type="secondary" style={{ marginLeft: 6 }}>
                      (+{inactiveList.length} inactive)
                    </Text>
                  ) : null}
                  {scheduledList.length > 0 ? (
                    <Text type="secondary" style={{ marginLeft: 6 }}>
                      (+{scheduledList.length} scheduled)
                    </Text>
                  ) : null}
                  {missingList.length > 0 ? (
                    <Text type="secondary" style={{ marginLeft: 6 }}>
                      (+{missingList.length} missing)
                    </Text>
                  ) : null}
                </span>
              </Tooltip>
            ) : (
                <Space orientation="vertical" size={2} style={{ width: "100%" }}>
                  {statusEntries.map((entry) => {
                    const status = entry.status.status as SchoolOfferingStatus;
                    const tooltip =
                        status === "ScheduledInactive"
                            ? `Scheduled decommission: ${formatDecommissionDate(entry.status.decommissionDate)}`
                            : status === "Inactive"
                                ? `Decommissioned: ${formatDecommissionDate(entry.status.decommissionDate)}`
                                : status === "Disabled"
                                    ? "Not created"
                                    : undefined;

                    return (
                        <Tooltip key={entry.id} title={tooltip}>
                          <div className="school-line">
                            <Text>{entry.name}</Text>
                            {renderStatusTag(status)}
                          </div>
                        </Tooltip>
                    );
                  })}
                </Space>
            );

          const schoolOptionsDetailed = schoolOptions.map((opt) => {
            const id = Number(opt.value);
            const status = resolveStatus(statusMap, id);
            const statusEntry = statusMap.get(id);
            const labelText = schoolNameMap.get(id) ?? `School ${id}`;

            let label: ReactNode = labelText;
            if (status === "Inactive") {
              const tooltip = statusEntry?.isDeleted
                ? "Deleted"
                : `Decommissioned: ${formatDecommissionDate(
                    statusEntry?.decommissionDate
                  )}`;
              label = (
                <Tooltip title={tooltip}>
                  <Text type="secondary">{labelText}</Text>
                </Tooltip>
              );
            } else if (status === "Disabled") {
              label = (
                <Tooltip title="Not created">
                  <Text type="secondary">{labelText}</Text>
                </Tooltip>
              );
            } else if (status === "ScheduledInactive") {
              label = (
                <Tooltip
                  title={`Scheduled decommission: ${formatDecommissionDate(
                    statusEntry?.decommissionDate
                  )}`}
                >
                  <Text>{labelText}</Text>
                </Tooltip>
              );
            }

            return { ...opt, label, labelText };
          });

          const child = (
              <SchoolsEditPopover
                selectedIds={selectedIds}
                options={schoolOptionsDetailed}
                offeringKey={offeringKeyOf(row)}
                rowEditable={(row.product as any).hasPendingChanges === false}
                viewContent={viewChild}
                onChange={(nextIds) => {
                  const toggled = new Set(nextIds);
                  const next = nextIds.map((id) => ({ id }));

                  handleChange(
                    (row.product as any).productId,
                    "schoolIds",
                    next,
                    row.product as any
                  );

                  const allIds = schoolOptions.map((opt) => Number(opt.value));
                  allIds.forEach((id) => {
                    const nextChecked = toggled.has(id);
                    const wasChecked = selectedIds.includes(id);
                    if (nextChecked !== wasChecked) {
                      onSchoolToggle(row.product, id, nextChecked);
                    }
                  });
                }}
              />
          );

          return {
            children: child,
            props: { rowSpan: row.productRowSpan },
          };
        }
        return { children: null, props: { rowSpan: 0 } };
      },
    },
    {
      title: (
        <span>
          Band Strategy{" "}
          <Tooltip
            title={
              <>
                <strong>Compound:</strong> Each price period is calculated
                separately based on its own bands. Weeks in one period do not
                affect pricing in another.
                <br />
                <br />
                <strong>Discrete:</strong> Weeks accumulate across periods, and
                pricing bands apply to the total number of weeks booked.
              </>
            }
          >
            <InfoCircleOutlined />
          </Tooltip>
        </span>
      ),
      dataIndex: ["period", "priceBandStrategyId"],
      width: PP_COL_W.bandStrategy,
      render: (_: any, row: FlatRow) => {
        if (row.pbi === 0) {
          return {
            children: (
              <PricelistFormField
                key={`bandStrategy-${row.pi}-${row.ppi}-${
                  (row.period as any).startDate
                }`}
                name={`bandStrategy-${row.pi}-${row.ppi}`}
                offeringKey={offeringKeyOf(row)}
                rowEditable={
                  (row.product as any).isNew === true &&
                  (row.product as any).hasPendingChanges === false
                }
                type="dropdown"
                defaultValue={
                  BandStrategy[(row.period as any).priceBandStrategyId]
                }
                required
                formErrors={formErrors}
                standalone={standaloneFields}
                onError={handleError}
                onChange={(value) => {
                  const numericId = (BandStrategy as any)[value as any];
                  handleChange(
                    (row.product as any).productId,
                    "priceBandStrategyId",
                    numericId,
                    (row.product as any).pricePeriods,
                    row.ppi
                  );
                }}
                dropdownOptions={Object.entries(BandStrategy)
                  .filter((b) => isNaN(Number(b[0])))
                  .map(([band, bandId]) => ({
                    key: String(bandId),
                    value: band,
                    text: band,
                  }))}
              />
            ),
            props: { rowSpan: row.periodRowSpan },
          };
        }
        return { children: null, props: { rowSpan: 0 } };
      },
    },
    {
      title: "Date Strategy",
      dataIndex: "dateStrategy",
      width: PP_COL_W.dateStrategy,
      render: (_: any, row: FlatRow) => {
        if (row.ppi === 0 && row.pbi === 0) {
          return {
            children: formatDateStrategy(
              row.product,
              (row.product as any).hasPendingChanges
            ),
            props: { rowSpan: row.productRowSpan },
          };
        }
        return { children: null, props: { rowSpan: 0 } };
      },
    },
    ...(selectedProductType === "Package"
      ? [
          {
            title: "Breakdowns",
            dataIndex: "breakdown",
            width: 260,
            render: (_: any, row: FlatRow) => {
              if (row.ppi === 0 && row.pbi === 0) {
                const breakdowns = ((row.product as any)
                  .packageOfferingBreakdowns ??
                  []) as PackageOfferingBreakdown[];

                if (!breakdowns.length) {
                  return {
                    children: <Text type="secondary">—</Text>,
                    props: { rowSpan: row.productRowSpan },
                  };
                }

                const vms = buildBreakdownVMs(
                  breakdowns,
                  breakdownNameMap,
                  productTypeNameFromId
                );
                const colorMap = buildBreakdownColorMap(vms);

                const inlineLimit = 2;
                const inlineItems = vms.slice(0, inlineLimit);
                const remaining = vms.length - inlineItems.length;

                const cell = (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => openBreakdownsModal(row.product)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openBreakdownsModal(row.product);
                      }
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <Space
                      orientation="vertical"
                      size={6}
                      style={{ width: "100%" }}
                    >
                      {inlineItems.map((x) => (
                        <BreakdownLegendLine
                          key={x.key}
                          x={x}
                          color={colorMap.get(x.key) ?? "rgba(0,0,0,0.25)"}
                        />
                      ))}

                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {remaining > 0
                          ? `+${remaining} more — click to view`
                          : "Click to view"}
                      </Text>
                    </Space>
                  </div>
                );

                return {
                  children: cell,
                  props: { rowSpan: row.productRowSpan },
                };
              }

              return { children: null, props: { rowSpan: 0 } };
            },
          },
        ]
      : []),
  ];

  return columns;
}
